/**
 * Toast Constants and Utilities
 * 
 * This file contains all toast-related constants, styles, and utilities
 * that can be used throughout the entire application. This ensures
 * consistency across all components and modules.
 */

// ==================== TOAST TYPES ====================
export const TOAST_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  INFO: 'info',
  WARNING: 'warning',
  TRANSFER: 'transfer'
};

// ==================== TOAST ICONS ====================
export const TOAST_ICONS = {
  SUCCESS: '✅',
  ERROR: '❌',
  INFO: 'ℹ️',
  WARNING: '⚠️',
  TRANSFER: '⏳',
  LOADING: '⏳',
  CHECK: '✓',
  CROSS: '✗',
  STAR: '⭐',
  HEART: '❤️',
  THUMBS_UP: '👍',
  THUMBS_DOWN: '👎'
};

// ==================== TOAST DURATIONS ====================
export const TOAST_DURATIONS = {
  SHORT: 2000,
  MEDIUM: 3000, // Standard duration for success toasts
  STANDARD: 4000, // Standard duration for error/info toasts
  EXTENDED: 5000, // Extended duration for notices that need extra time
  LONG: 7000,
  EXTRA_LONG: 8000, // Rare high-priority notifications
  PERSISTENT: 0 // 0 means it won't auto-dismiss
};

// ==================== TOAST POSITION ====================
export const TOAST_POSITION = {
  TOP_LEFT: 'top-left',
  TOP_CENTER: 'top-center',
  TOP_RIGHT: 'top-right',
  BOTTOM_LEFT: 'bottom-left',
  BOTTOM_CENTER: 'bottom-center',
  BOTTOM_RIGHT: 'bottom-right',
};

// Default position for all toasts
export const DEFAULT_TOAST_POSITION = TOAST_POSITION.TOP_CENTER;

// ==================== BASE TOAST STYLE ====================
// Base style shared by all toast types - single source of truth
export const BASE_TOAST_STYLE = {
  background: '#ffffff',
  color: '#1f2937',
  borderRadius: '12px',
  padding: '16px 20px',
  fontSize: '14px',
  fontWeight: '500',
};

// ==================== DEFAULT ICON THEME ====================
// Default icon theme fallback - uses brand color
export const DEFAULT_ICON_THEME = {
  primary: '#2563eb', // Brand primary color
  secondary: '#ffffff',
};

// ==================== INLINE CSS STYLES ====================
// These maintain the original design for backward compatibility
export const TOAST_STYLES = {
  success: {
    icon: TOAST_ICONS.SUCCESS,
    duration: TOAST_DURATIONS.MEDIUM, // Success toasts are shorter (3000ms)
    style: {
      background: '#ffffff',
      color: '#1f2937',
      border: '2px solid #2563eb', // Brand primary color (matches App.js)
      borderRadius: '12px',
      padding: '16px 20px',
      fontSize: '14px',
      fontWeight: '500',
      boxShadow: '0 10px 25px -5px rgba(49, 117, 180, 0.3)',
    },
    iconTheme: {
      primary: '#2563eb',
      secondary: '#ffffff',
    }
  },
  
  error: {
    icon: TOAST_ICONS.ERROR,
    duration: TOAST_DURATIONS.STANDARD,
    style: {
      background: '#ffffff',
      color: '#1f2937',
      border: '2px solid #ef4444', // Red-500
      borderRadius: '12px',
      padding: '16px 20px',
      fontSize: '14px',
      fontWeight: '500',
      boxShadow: '0 10px 25px -5px rgba(239, 68, 68, 0.3)',
    },
    iconTheme: {
      primary: '#ef4444',
      secondary: '#ffffff',
    }
  },
  
  info: {
    icon: TOAST_ICONS.INFO,
    duration: TOAST_DURATIONS.STANDARD,
    style: {
      background: '#ffffff',
      color: '#1f2937',
      border: '2px solid #2563eb', // Brand primary color (matches App.js)
      borderRadius: '12px',
      padding: '16px 20px',
      fontSize: '14px',
      fontWeight: '500',
      boxShadow: '0 10px 25px -5px rgba(49, 117, 180, 0.3)',
    },
    iconTheme: {
      primary: '#2563eb',
      secondary: '#ffffff',
    }
  },
  
  warning: {
    icon: TOAST_ICONS.WARNING,
    duration: TOAST_DURATIONS.STANDARD,
    style: {
      background: '#ffffff',
      color: '#1f2937',
      border: '2px solid #f59e0b', // Amber-500
      borderRadius: '12px',
      padding: '16px 20px',
      fontSize: '14px',
      fontWeight: '500',
      boxShadow: '0 10px 25px -5px rgba(245, 158, 11, 0.3)',
    },
    iconTheme: {
      primary: '#f59e0b',
      secondary: '#ffffff',
    }
  },
  
  transfer: {
    icon: TOAST_ICONS.TRANSFER,
    duration: TOAST_DURATIONS.STANDARD,
    style: {
      background: '#ffffff',
      color: '#1f2937',
      border: '2px solid #8b5cf6', // Purple-500
      borderRadius: '12px',
      padding: '16px 20px',
      fontSize: '14px',
      fontWeight: '500',
      boxShadow: '0 10px 25px -5px rgba(139, 92, 246, 0.3)',
    },
    iconTheme: {
      primary: '#8b5cf6',
      secondary: '#ffffff',
    }
  }
};

// ==================== TAILWIND CSS CLASSES ====================
export const TOAST_CLASSES = {
  // Base container classes
  container: 'bg-white text-gray-800 border-2 rounded-xl px-5 py-4 text-sm font-medium shadow-lg',
  
  // Type-specific classes
  success: 'border-green-500 shadow-green-200',
  error: 'border-red-500 shadow-red-200',
  info: 'border-blue-500 shadow-blue-200',
  warning: 'border-amber-500 shadow-amber-200',
  transfer: 'border-purple-500 shadow-purple-200',
  
  // Size variants
  small: 'px-3 py-2 text-xs',
  medium: 'px-5 py-4 text-sm',
  large: 'px-6 py-5 text-base',
  
  // Position variants
  top: 'top-4',
  bottom: 'bottom-4',
  center: 'top-1/2 transform -translate-y-1/2'
};

// ==================== TAILWIND TOAST CONFIGURATIONS ====================
export const TAILWIND_TOAST_CONFIGS = {
  success: {
    icon: TOAST_ICONS.SUCCESS,
    duration: TOAST_DURATIONS.MEDIUM,
    className: `${TOAST_CLASSES.container} ${TOAST_CLASSES.success}`
  },
  error: {
    icon: TOAST_ICONS.ERROR,
    duration: TOAST_DURATIONS.STANDARD,
    className: `${TOAST_CLASSES.container} ${TOAST_CLASSES.error}`
  },
  info: {
    icon: TOAST_ICONS.INFO,
    duration: TOAST_DURATIONS.STANDARD,
    className: `${TOAST_CLASSES.container} ${TOAST_CLASSES.info}`
  },
  warning: {
    icon: TOAST_ICONS.WARNING,
    duration: TOAST_DURATIONS.STANDARD,
    className: `${TOAST_CLASSES.container} ${TOAST_CLASSES.warning}`
  },
  transfer: {
    icon: TOAST_ICONS.TRANSFER,
    duration: TOAST_DURATIONS.STANDARD,
    className: `${TOAST_CLASSES.container} ${TOAST_CLASSES.transfer}`
  }
};

// ==================== UTILITY FUNCTIONS ====================

/**
 * Get toast configuration by type
 * @param {string} type - The type of toast (success, error, info, warning, transfer)
 * @param {string} message - The message to display
 * @param {number} duration - Optional custom duration
 * @returns {Object} Toast configuration object
 */
export const getToastConfig = (type, message, duration = null) => {
  const config = TOAST_STYLES[type] || TOAST_STYLES.info;
  return {
    ...config,
    duration: duration || config.duration,
    ...(message && { message })
  };
};

/**
 * Get Tailwind-based toast configuration
 * @param {string} type - The type of toast
 * @param {string} message - The message to display
 * @param {number} duration - Optional custom duration
 * @returns {Object} Tailwind-based toast configuration
 */
export const getTailwindToastConfig = (type, message, duration = null) => {
  const config = TAILWIND_TOAST_CONFIGS[type] || TAILWIND_TOAST_CONFIGS.info;
  return {
    ...config,
    duration: duration || config.duration,
    ...(message && { message })
  };
};

/**
 * Get toast configuration with custom size
 * @param {string} type - The type of toast
 * @param {string} message - The message to display
 * @param {string} size - The size variant (small, medium, large)
 * @param {number} duration - Optional custom duration
 * @returns {Object} Toast configuration with custom size
 */
export const getToastConfigWithSize = (type, message, size = 'medium', duration = null) => {
  const baseConfig = getTailwindToastConfig(type, message, duration);
  const sizeClass = TOAST_CLASSES[size] || TOAST_CLASSES.medium;
  
  return {
    ...baseConfig,
    className: `${baseConfig.className} ${sizeClass}`
  };
};

// ==================== COMMON TOAST MESSAGES ====================
export const TOAST_MESSAGES = {
  // Chat related messages
  CHAT_ACCEPTED: 'Chat accepted successfully!',
  CHAT_REJECTED: 'Chat rejected successfully!',
  TRANSFER_ACCEPTED: 'Transfer accepted successfully!',
  TRANSFER_REJECTED: 'Transfer rejected successfully!',
  MESSAGE_SENT: 'Message sent successfully!',
  MESSAGE_FAILED: 'Failed to send message',
  CHAT_PINNED: 'Chat pinned',
  CHAT_UNPINNED: 'Chat unpinned',
  
  // Feature coming soon messages
  REACTION_COMING_SOON: 'Reaction feature coming soon!',
  FORWARD_COMING_SOON: 'Forward feature coming soon!',
  
  // Error messages
  NETWORK_ERROR: 'Network error: No response from server',
  SERVER_ERROR: 'Server error occurred',
  VALIDATION_ERROR: 'Please check your input',
  
  // Success messages
  OPERATION_SUCCESS: 'Operation completed successfully!',
  SAVED_SUCCESS: 'Changes saved successfully!',
  UPDATED_SUCCESS: 'Updated successfully!',
  DELETED_SUCCESS: 'Deleted successfully!'
};

// ==================== TOAST PRESETS ====================
export const TOAST_PRESETS = {
  // Quick success toast
  quickSuccess: (message) => getToastConfig(TOAST_TYPES.SUCCESS, message, TOAST_DURATIONS.SHORT),
  
  // Quick error toast
  quickError: (message) => getToastConfig(TOAST_TYPES.ERROR, message, TOAST_DURATIONS.SHORT),
  
  // Persistent loading toast
  loading: (message) => getToastConfig(TOAST_TYPES.INFO, message, TOAST_DURATIONS.PERSISTENT),
  
  // Long duration warning
  warning: (message) => getToastConfig(TOAST_TYPES.WARNING, message, TOAST_DURATIONS.LONG),
  
  // Transfer notification
  transfer: (message) => getToastConfig(TOAST_TYPES.TRANSFER, message, TOAST_DURATIONS.STANDARD)
};

// ==================== TOASTER COMPONENT CONFIGURATION ====================
/**
 * Generate centralized Toaster component configuration
 * This is the SINGLE SOURCE OF TRUTH for all Toaster props
 * Used in App.js to configure the react-hot-toast Toaster component
 * 
 * @param {Object} options - Optional overrides
 * @param {string} options.position - Toast position (default: TOP_CENTER)
 * @param {Object} options.defaultStyle - Default style for all toasts
 * @returns {Object} Toaster configuration object for react-hot-toast
 * 
 * @example
 * // In App.js
 * import { getToasterConfig } from './constants/toastConstants';
 * <Toaster {...getToasterConfig()} />
 */
export const getToasterConfig = (options = {}) => {
  const {
    position = DEFAULT_TOAST_POSITION,
    defaultStyle = BASE_TOAST_STYLE
  } = options;

  return {
    position,
    // Above every overlay in the app, including the highest modal/sheet/dropdown
    // layers (BaseModal, Sheet and InactiveChatReminder use z-[100000]/z-[100001],
    // BitrixFieldDropdown uses z-[120000]) so toasts are never hidden behind a
    // backdrop when a modal stays open after a failed save.
    containerStyle: {
      zIndex: 130000,
    },
    toastOptions: {
      // Default options for all toasts
      duration: TOAST_DURATIONS.STANDARD,
      style: defaultStyle,
      
      // Type-specific configurations
      // Icons are set here to ensure consistency - single source of truth
      success: {
        duration: TOAST_STYLES.success.duration,
        style: TOAST_STYLES.success.style,
        icon: TOAST_STYLES.success.icon, // ✅ from TOAST_ICONS.SUCCESS
        iconTheme: TOAST_STYLES.success.iconTheme,
      },
      error: {
        duration: TOAST_STYLES.error.duration,
        style: TOAST_STYLES.error.style,
        icon: TOAST_STYLES.error.icon, // ❌ from TOAST_ICONS.ERROR
        iconTheme: TOAST_STYLES.error.iconTheme,
      },
      info: {
        duration: TOAST_STYLES.info.duration,
        style: TOAST_STYLES.info.style,
        icon: TOAST_STYLES.info.icon, // ℹ️ from TOAST_ICONS.INFO
        iconTheme: TOAST_STYLES.info.iconTheme,
      },
      warning: {
        duration: TOAST_STYLES.warning.duration,
        style: TOAST_STYLES.warning.style,
        icon: TOAST_STYLES.warning.icon, // ⚠️ from TOAST_ICONS.WARNING
        iconTheme: TOAST_STYLES.warning.iconTheme,
      },
      transfer: {
        duration: TOAST_STYLES.transfer.duration,
        style: TOAST_STYLES.transfer.style,
        icon: TOAST_STYLES.transfer.icon, // ⏳ from TOAST_ICONS.TRANSFER
        iconTheme: TOAST_STYLES.transfer.iconTheme,
      },
      loading: {
        duration: TOAST_DURATIONS.PERSISTENT, // Loading toasts don't auto-dismiss
        style: TOAST_STYLES.info.style, // Use info style for loading
        icon: TOAST_ICONS.LOADING, // ⏳ from TOAST_ICONS.LOADING
        iconTheme: TOAST_STYLES.info.iconTheme,
      },
    }
  };
};
